import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2, Plus } from 'lucide-react'
import { ContentBlockSelector } from '@/containers/ContentBlocks/components/ContentBlockSelector'
import { DraggableContentBlockList } from '@/containers/ContentBlocks/components/DraggableContentBlockList'
import { ContentBlockItem } from '@/containers/ContentBlocks/components/DraggableContentBlockItem'

interface PageContentBlocksPanelProps {
  contentBlocks: ContentBlockItem[]
  setContentBlocks: React.Dispatch<React.SetStateAction<ContentBlockItem[]>>
  /** Block currently open in the right-column editor (or 'new', or null). */
  selectedBlockId: number | 'new' | null
  /** Open a block in the right-column editor (number/'new'), or close it (null). */
  onSelectBlock: (id: number | 'new' | null) => void
  /** Merge an inline description edit into the owner's block state. */
  onDescriptionSaved: (block: ContentBlockItem) => void
  /** Stage an inline description edit into the page's per-block draft. */
  onStageDescription: (blockId: number, language: string, value: string) => void
  /** Stage an inline title edit into the page's per-block draft. */
  onStageName: (blockId: number, language: string, value: string) => void
  /**
   * Open the remove-block choice dialog (remove from page vs delete
   * permanently). The dialog + removal mutation now live in PageDetails so the
   * right-panel Delete button can share the same flow.
   */
  onRequestDelete: (blockId: number) => void
  /**
   * Immediately create a new block with default values (no form) and select it.
   * Owned by PageDetails (which has the CREATE_CONTENT_BLOCK mutation +
   * handleBlockCreated). Optional/backward-compatible.
   */
  onCreateNewBlock?: () => void
  /** Pending state for the create mutation (disables the create button). */
  isCreating?: boolean
}

export default function PageContentBlocksPanel({
  contentBlocks,
  setContentBlocks,
  selectedBlockId,
  onSelectBlock,
  onDescriptionSaved,
  onStageDescription,
  onStageName,
  onRequestDelete,
  onCreateNewBlock,
  isCreating = false,
}: PageContentBlocksPanelProps) {
  const [selectorOpen, setSelectorOpen] = useState(false)

  // --- order / membership ---

  const handleReorder = (reorderedBlocks: ContentBlockItem[]) => {
    setContentBlocks(reorderedBlocks)
  }

  // "Attach existing" — append the chosen block and open it in the right editor.
  const handleAddBlock = (block: ContentBlockItem) => {
    setContentBlocks(prev => [...prev, { ...block, arrangement: prev.length }])
    setSelectorOpen(false)
    onSelectBlock(block.id)
  }

  // "Create new" — open an empty editor in the right panel; PageDetails appends
  // the block once it is created (handleBlockCreated).
  const handleCreateNew = () => {
    onSelectBlock('new')
  }

  // --- edit (open in right panel) ---

  const handleEditBlock = (blockId: number) => {
    onSelectBlock(blockId)
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Content Blocks</CardTitle>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onCreateNewBlock?.()}
            disabled={isCreating}
          >
            {isCreating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Create New
          </Button>
          <Button type="button" onClick={() => setSelectorOpen(true)}>
            Add Block
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {contentBlocks.length > 0 ? (
          <DraggableContentBlockList
            blocks={contentBlocks}
            onReorder={handleReorder}
            onRemove={onRequestDelete}
            onEdit={handleEditBlock}
            onClose={() => onSelectBlock(null)}
            fullSize
            selectedBlockId={selectedBlockId}
            onDescriptionSaved={onDescriptionSaved}
            onStageDescription={onStageDescription}
            onStageName={onStageName}
          />
        ) : (
          <p className="text-muted-foreground text-sm">
            No content blocks assigned to this page.
          </p>
        )}

        <ContentBlockSelector
          open={selectorOpen}
          onOpenChange={setSelectorOpen}
          assignedBlockIds={contentBlocks.map(b => b.id)}
          onSelect={handleAddBlock}
          onCreateNew={handleCreateNew}
        />
      </CardContent>
    </Card>
  )
}
